<template>
  <card-component
    truncate
    class="resource question"
    size="xsmall"
    :class="[
      {
        moe: checkIfMoe,
        community: checkIfCommunity,
      },
      selectedClass,
    ]"
    @on-click="handlePrimaryCardAction"
  >
    <template #label>
      <span class="location">{{ location(resourceData.resourceDomain) }}</span>
    </template>
    <template #thumbnail>
      <component :is="thumbnail" class="thumbnail" />
    </template>

    <template #header>
      <h6 class="title">{{ resourceData.title }}</h6>
    </template>
    <template #metadata>
      <li>{{ questionTypeLabel }}</li>
      <li v-if="subjectLabel" class="subject">{{ subjectLabel }}</li>
    </template>
    <template v-if="!hideSelectAction" #actions>
      <cv-button
        v-if="isSelection"
        class="icon-button view-details"
        kind="ghost"
        @click="handleViewDetails"
      >
        <svg-icon name="Details24" kind="action" />
      </cv-button>
      <cv-button
        v-if="isMultipleSelection"
        class="icon-button multiple-selection"
        :class="{ _selected: isSelectedCard }"
        @click.stop="handleSelect"
      >
        <svg-icon :name="selectActionIcon" kind="forms" />
      </cv-button>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import { isEmpty } from "@utils/var.check";
import SearchResourceDetailContentSubpage from "@modules/search/components/SearchResourceDetailContentSubpage";
import {
  NAMESPACE,
  QUESTION_TYPE,
  ADD_FROM_LOCATION,
} from "@modules/search/service/search.constant";
import MultipleChoiceThumbnail from "@images/lesson/Question/MultipleChoice.svg?component";
import MultipleResponseThumbnail from "@images/lesson/Question/MultipleResponse.svg?component";
import AudioRecordingThumbnail from "@images/lesson/Question/AudioRecording.svg?component";
import FreeResponseThumbnail from "@images/lesson/Question/FreeResponse.svg?component";
import FillInTheBlankThumbnail from "@images/lesson/Question/FillInTheBlank.svg?component";
import ClickAndDropThumbnail from "@images/lesson/Question/ClickAndDrop.svg?component";
import ErrorEditingThumbnail from "@images/lesson/Question/ErrorEditing.svg?component";
import MultiplePartThumbnail from "@images/lesson/Question/MultiPartQuestion.svg?component";
import SearchSelectionMixin from "@modules/search/mixin/search.selection.mixin";
import LessonCardMixin from "@plugins/common/mixin/lesson.card.mixin";
import ResourceMixin from "@plugins/common/mixin/resource.mixin";
import { computed, watch, onMounted, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

// Mixin
defineOptions({
  mixins: [SearchSelectionMixin, LessonCardMixin, ResourceMixin],
});

// Inject
const searchContext = inject("searchContext");

// Props
interface Props {
  resourceData?: Record<string, unknown>;
  fileType?: string | null;
  size?: string;
  searchFilters?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  resourceData: null,
  fileType: null,
  size: "xsmall",
});

// Emits
const emit = defineEmits<{
  actionBack: [];
  selectionSaved: [data: unknown];
  selectionChanged: [];
  resourceUnstarred: [data: unknown];
}>();

// Composables
const { t } = useI18n();
const authStore = useStore();
const { vm } = useVm();
const contentSubpageInstance = useContentSubpageInstance();

// Computed
const isStudent = computed(() => authStore.getters[`${NAMESPACE.AUTH}/isStudent`]);

const questionType = computed(() => {
  if (!isEmpty(props.resourceData.type)) {
    return props.resourceData.type;
  }
  return props.resourceData.questionType;
});

const questionTypeLabel = computed(() => {
  switch (questionType.value) {
    case QUESTION_TYPE.MCQ:
      return t("search_result_mcq_label");
    case QUESTION_TYPE.MRQ:
      return t("search_result_mrq_label");
    case QUESTION_TYPE.REC:
      return t("search_result_arq_label");
    case QUESTION_TYPE.FREE:
      return t("search_result_frq_label");
    case QUESTION_TYPE.FILE:
      return t("search_result_fsq_label");
    case QUESTION_TYPE.FILL:
      return t("search_result_fitb_label");
    case QUESTION_TYPE.DND:
      return t("search_result_cnd_label");
    case QUESTION_TYPE.EEQ:
      return t("search_result_eeq_label");
    case QUESTION_TYPE.MPQ:
      return t("search_result_mpq_label");
    default:
      return "";
  }
});

const thumbnail = computed(() => {
  switch (questionType.value) {
    case QUESTION_TYPE.MCQ:
      return MultipleChoiceThumbnail;
    case QUESTION_TYPE.MRQ:
      return MultipleResponseThumbnail;
    case QUESTION_TYPE.REC:
      return AudioRecordingThumbnail;
    case QUESTION_TYPE.FREE:
    case QUESTION_TYPE.FILE:
      return FreeResponseThumbnail;
    case QUESTION_TYPE.FILL:
      return FillInTheBlankThumbnail;
    case QUESTION_TYPE.DND:
      return ClickAndDropThumbnail;
    case QUESTION_TYPE.EEQ:
      return ErrorEditingThumbnail;
    case QUESTION_TYPE.MPQ:
      return MultiplePartThumbnail;
    default:
      return MultipleChoiceThumbnail;
  }
});

const actionTriggeredOnCardClick = computed(() => {
  // Based on card patterns, the action that should trigger upon clicking the card
  // is the card's last available action.
  // Hence checking of which action is allowed must be done in sequence.
  if (hideSelectAction.value) return handleViewDetails;
  if (vm.isMultipleSelection) return handleSelect;
  if (vm.isSingleSelection) return handleChooseSelection;
  return handleViewDetails;
});

const selectActionIcon = computed(() => {
  return vm.isSelectedCard ? "RadioTicked24" : "Radio24";
});

const isAddFromLibraryOrStarredResources = computed(() => {
  if (!searchContext.addFromLocation) return false;
  return (
    searchContext.addFromLocation === ADD_FROM_LOCATION.LIBRARY ||
    searchContext.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES
  );
});

const hasSelectAction = computed(() => {
  return !isStudent.value && isAddFromLibraryOrStarredResources.value;
});

const hideSelectAction = computed(() => {
  return (
    (searchContext.addFromMpqChildLayer || searchContext.addFromMpqSubLayer) &&
    questionType.value === QUESTION_TYPE.MPQ
  );
});

// Methods
const handleViewDetails = () => {
  contentSubpageInstance.show(
    SearchResourceDetailContentSubpage,
    {
      baseTitle: vm.subpageTitle,
      hasParentSubpage: vm.isSelection,
      uuid: props.resourceData.uuid,
      title: props.resourceData.title,
      type: "QUESTION",
      location: vm.location(props.resourceData.resourceDomain),
      hasSelectAction: hasSelectAction.value,
      addFromLocation: searchContext.addFromLocation,
      addFromMpqChildLayer: searchContext.addFromMpqChildLayer,
      addFromMpqSubLayer: searchContext.addFromMpqSubLayer,
      searchFilters: props.searchFilters,
      actionBack: () => {
        if (hasSelectAction.value) {
          vm.store.clearQuestionPartsSelectionList();
        }
        emit("actionBack");
      },
    },
    {
      resourceSelected: handleResourceSelected,
      resourceUnstarred: handleResourceUnstarred,
    },
  );
};

const handleResourceUnstarred = () => {
  if (searchContext?.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES) {
    emit("resourceUnstarred", props.resourceData.uuid);
  }
};

const handlePrimaryCardAction = () => {
  const action = actionTriggeredOnCardClick.value;
  action();
};

const handleResourceSelected = (data) => {
  if (!data.isAddingFromQuestionParts) {
    vm.addSelection();
  }

  emit("selectionSaved", data);
};

const handleChooseSelection = () => {
  vm.addSelection();
  emit("selectionSaved");
};

const handleSelect = () => {
  vm.isSelectedCard = !vm.isSelectedCard;
  if (vm.isSelectedCard) {
    vm.addSelection();
  } else {
    vm.removeSelection(props.resourceData?.uuid);
  }
  emit("selectionChanged");
};

// Lifecycle hooks
onMounted(() => {
  if (props.searchFilters) {
    vm.sortResourceTaggingsAndGenerateSubjectLevelList();
  }
  const selection = vm.store.getSelectionByUuid(props.resourceData.uuid);
  vm.isSelectedCard = !isEmpty(selection);

  // Watch
  watch(
    () => vm.store,
    () => {
      vm.handleClearSelection();
    },
    { deep: true },
  );
});
</script>
